import { type ApiCallChain, type ChainingFunction, configureApi } from "@api/integration";
import { Configuration } from "@api/configuration";
import { hideLoadingPanel, showLoadingPanel } from "@services/loading-service";
import { AxiosError, AxiosHeaders, type AxiosResponse, type InternalAxiosRequestConfig } from "axios";
import { ApiError, ApiErrorFactory } from ".";
import { DevExtremeDataErrorFactory } from "../devextreme/devextreme-data-error";
import { AppError, useErrorNotifier } from "../errorHandling";

// noinspection JSUnusedGlobalSymbols
export interface IApiCallConfiguration {
    showLoading?: boolean;
    errorHandling?: boolean | ((apiError: ApiError, error: AxiosError) => Promise<any>);
    isBlob?: boolean;
    isFile?: boolean;
    getFileName?: ((fileName: string | null | undefined, headers: AxiosHeaders) => string);
}

/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-assignment */
function setupLoadingPanel(chain: ChainingFunction<unknown[], unknown>):
    (...args: unknown[]) => Promise<ApiCallChain<unknown[], unknown>> {

    return async (...args: unknown[]) => {
        const config = await (chain(...args));
        const apiConfig = (config.axiosConfiguration as IApiCallConfiguration);

        if (apiConfig.showLoading !== false) {
            config.axiosInstance.interceptors.request.use((request: InternalAxiosRequestConfig) => {
                config.axiosConfiguration.extensions.operationId = showLoadingPanel();
                return request;
            });

            config.axiosInstance.interceptors.response.use((response: AxiosResponse) => {
                hideLoadingPanel(config.axiosConfiguration.extensions.operationId as string);
                return response;
            }, async (error: AxiosError) => {
                hideLoadingPanel(config.axiosConfiguration.extensions.operationId as string);
                return Promise.reject(error);
            });
        }

        // TODO RUBÉN: FUTURO: ESTO TENDRÍA QUE TENER SU PROPIO MIDDLEWARE
        if (apiConfig.isBlob === true || apiConfig.isFile === true)  {
            config.axiosConfiguration.responseType = "blob";
        }
        return config;
    }
}

function setupErrorHandling(chain: ChainingFunction<unknown[], unknown>): (...args: unknown[]) => Promise<ApiCallChain<unknown[], unknown>> {
    return async (...args: unknown[]) => {
        const config = await (chain(...args));
        config.axiosInstance.interceptors.response.use((response: AxiosResponse) => {
            return response;
        }, async (error: any) => {

            const cfg = config.axiosConfiguration as IApiCallConfiguration;
            const apiError = ApiErrorFactory.fromAxiosError(error);
            if (typeof cfg.errorHandling == "function")
                error = await cfg.errorHandling(apiError, error);
            else if (cfg.errorHandling !== false) {
                error = apiError;
            }

            if (error instanceof Error)
                return Promise.reject(error);
            return Promise.resolve(error);
        });
        return config;
    }
}


const dateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(.\d*)?$/;

export function convertirAFecha(key: any, value: any) {
    if (typeof value === 'string' && dateFormat.test(value)) {
        return new Date(value);
    }
    return value;
}


export function setupApi() {
    configureApi({
        baseURL: import.meta.env.VITE_API_BASE_URL,
        withCredentials: true,
        transformResponse: function (datos: string, headers, status) {
            const config = this as IApiCallConfiguration;
            const h = headers as AxiosHeaders;
            const contentType = (h.hasContentType() ? h["content-type"] : "").toLowerCase();
            const isJson = contentType.startsWith("application/json") || contentType.startsWith("application/problem+json");

            if (isJson)
                return JSON.parse(datos, convertirAFecha)

            if (config.isFile === true){
                let fileName :string | null = null;
                const regex = /filename=([^;]*).*$/;
                const result = regex.exec(h['content-disposition']);
                if (result != null)
                    fileName = result[1];
                if (config.getFileName != null)
                    fileName = config.getFileName(fileName, h);
                if ((fileName == null) || (typeof fileName === 'string' && fileName.trim() === ''))
                    throw new AppError('api', '', 'No se ha podido obtener el nombre del fichero; ¿existe?');
                return new File([datos], fileName);
            }
            return datos;
        }
    },
        new Configuration({}),
        {
            initialMiddleware: [setupLoadingPanel],
            finalMiddleware: [setupErrorHandling], dxDataErrorHandler: (e) => {
                useErrorNotifier().notifyError(DevExtremeDataErrorFactory.fromError(e, "DXData"));
            }
        }
    );
}